import React, { useState, useEffect, useRef, useMemo, useCallback, useImperativeHandle, type Ref } from 'react';
import { ActiveLicenses, CreateShopModel, SensorCameraCredentials, Shop, ShopAddress } from '@core/types';
import { useTranslation } from 'react-i18next';
import { required } from '@core/utils/validation.ts';
import { Box, Collapse, Grid, IconButton, Paper, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useApiStore, useAuthStore } from '@core/store';
import { shopService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import DayOfWeekFromUntil from '../shops/DayOfWeekFromUntil.tsx';
import { DeleteConfirmationDialog, FormContainer, WcnButton } from '@/components';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import ConfirmationDialog from '@/components/dialog/ConfirmationDialog.tsx';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import ShopSelector from '@/components/selectors/ShopSelector.tsx';
import ToggleButton from '../../../../components/shared/ToggleButton.tsx';

export enum ShopEditorMode {
    Create = 'Create',
    Edit = 'Edit'
}

const Section: React.FC<{
    title: string;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}> = ({ title, expanded, onToggle, children }) => (
    <Box sx={{ px: 2, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={onToggle} sx={{ p: 0.25 }}>
                <ExpandMore
                    sx={{
                        fontSize: 20,
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                    }}
                />
            </IconButton>
            <Typography
                variant="subtitle2"
                onClick={onToggle}
                sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    userSelect: 'none'
                }}
            >
                {title}
            </Typography>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
            <Box sx={{ pl: { xs: 3.5, md: 24 }, pr: { xs: 1, md: 32 }, pt: 2.5, pb: 2 }}>{children}</Box>
        </Collapse>
    </Box>
);

export interface ShopInformationRef {
    isDirty: boolean;
    save: () => Promise<boolean>;
}

const ShopInformation: React.FC<{
    shop: Shop | null;
    onClose: (shopId?: string) => void;
    onCancel: () => void;
    mode?: ShopEditorMode;
    loadingData: boolean;
    initialReferenceShopId?: string | null;
    ref?: Ref<ShopInformationRef>;
}> = ({ shop, onClose, onCancel, loadingData = false, mode = ShopEditorMode.Edit, initialReferenceShopId, ref }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<Shop>({} as Shop);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({
        referenceShop: true,
        general: true,
        address: true,
        sensorCameraCredentials: true,
        dataCollectionPeriod: true
    });
    const { hasLicense } = useAuthStore();
    const { shopIds, loadShopIds } = useApiStore();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { notifySuccess, notifyWarning, notifyErrorResponse, notifyWarningResponse } = useNotifications();
    const formRef = useRef<FormContainerRef>(null);
    const confirmProductionDialogRef = useRef<any>(null);
    const confirmationDialogRef = useRef<any>(null);
    const [formValid, setFormValid] = useState(true);
    const [originalShop, setOriginalShop] = useState<Shop | null>(null);
    const [dialogHeading, setDialogHeading] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [validatedAddress, setValidatedAddress] = useState<any>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const saveAttemptedRef = useRef(false);
    const skipNextReferenceShopValidateRef = useRef(false);
    const [referenceShopId, setReferenceShopId] = useState<string | null>(null);
    const [gateway, setGateway] = useState<string | null>(null);

    const toggleSection = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    const buildNewShop = (): Shop =>
        ({
            id: '',
            name: '',
            description: '',
            externalSystemShopIdentifier: '',
            shopDisplayName: '',
            address: {
                id: crypto.randomUUID(),
                shopId: null,
                street: '',
                postalCode: '',
                city: '',
                country: ''
            },
            dataCollectionPeriod: [
                { dayOfWeek: 1, from: '00:00', until: '23:59', isDataCollectionDisabled: false },
                { dayOfWeek: 2, from: '00:00', until: '23:59', isDataCollectionDisabled: false },
                { dayOfWeek: 3, from: '00:00', until: '23:59', isDataCollectionDisabled: false },
                { dayOfWeek: 4, from: '00:00', until: '23:59', isDataCollectionDisabled: false },
                { dayOfWeek: 5, from: '00:00', until: '23:59', isDataCollectionDisabled: false },
                { dayOfWeek: 6, from: '00:00', until: '23:59', isDataCollectionDisabled: false },
                { dayOfWeek: 0, from: '00:00', until: '23:59', isDataCollectionDisabled: false }
            ],
            sensorCameraCredentials: {
                username: '',
                password: ''
            },
            collectData24Hours: false,
            fastLanerGroupIds: [],
            gateways: []
        }) as Shop;

    useEffect(() => {
        if (mode === ShopEditorMode.Create) {
            const emptyShop = buildNewShop();
            setFormData(emptyShop);
            setOriginalShop(structuredClone(emptyShop));
            saveAttemptedRef.current = false;
            setValidatedAddress(null);
            if (initialReferenceShopId) {
                skipNextReferenceShopValidateRef.current = true;
            }
            setReferenceShopId(initialReferenceShopId ?? null);
            return;
        }
        if (!shop) {
            setFormData({} as Shop);
            setOriginalShop(null);
            return;
        }

        setFormData(shop);
        setOriginalShop(structuredClone(shop));
        saveAttemptedRef.current = false;
        setValidatedAddress(null);
    }, [shop]);

    useEffect(() => {
        if (referenceShopId) {
            if (skipNextReferenceShopValidateRef.current) {
                skipNextReferenceShopValidateRef.current = false;
                return;
            }
            formRef.current?.validate?.();
        }
    }, [referenceShopId]);

    const addressValidation = useCallback(
        (field: string) => {
            return (value: string) => {
                if (!validatedAddress || !value || validatedAddress[field] === value) return '';
                return t('suggestedChange', {
                    original: value,
                    updated: validatedAddress[field]
                });
            };
        },
        [validatedAddress, t]
    );

    const streetRules = useMemo(() => [required(t), addressValidation('street')], [t, addressValidation]);
    const postalCodeRules = useMemo(() => [required(t), addressValidation('postalCode')], [t, addressValidation]);
    const cityRules = useMemo(() => [required(t), addressValidation('city')], [t, addressValidation]);
    const countryRules = useMemo(() => [required(t), addressValidation('country')], [t, addressValidation]);

    const shopDisplayNameRule = useMemo(() => {
        return () => {
            const name = formData.shopDisplayName;
            if (!name) return '';
            if (name.length > 20) return t('validation.inputMaxLength', { length: 20 });
            return name.includes('!') || name.includes(':') ? t('forbiddenSpecialCharacters') : '';
        };
    }, [formData.shopDisplayName, t]);

    const shopIdRule = useMemo(() => {
        return () => {
            const id = formData.externalSystemShopIdentifier;
            if (!id) return '';
            return id.length > 10 ? t('validation.inputMaxLength', { length: 10 }) : '';
        };
    }, [formData.externalSystemShopIdentifier, t]);

    useEffect(() => {
        if (validatedAddress) {
            formRef.current?.validate?.();
        }
    }, [validatedAddress, t]);

    const handleChange = (field: keyof Shop, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleChangeAddress = (field: keyof ShopAddress, value: any) => {
        setFormData((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value
            }
        }));
        setValidatedAddress(null);
    };

    const handleChangeSensorCameraCredentials = (field: keyof SensorCameraCredentials, value: any) => {
        setFormData((prev) => ({
            ...prev,
            sensorCameraCredentials: {
                username: prev.sensorCameraCredentials?.username || '',
                password: prev.sensorCameraCredentials?.password || '',
                [field]: value
            }
        }));
    };

    const hasAnyCameraCredentials = (credentials?: SensorCameraCredentials) => {
        return Boolean(credentials?.username || credentials?.password);
    };

    const checkValidForm = (): boolean => {
        if (!!referenceShopId && !gateway) {
            notifyWarning('gatewayForReferenceShopMissing');
            return false;
        }

        const isValid = formRef.current?.validate?.() ?? formValid;
        saveAttemptedRef.current = true;

        if (!isValid) {
            notifyWarning(formRef.current?.getRequiredWarning() || 'required');
            return false;
        }

        return true;
    };

    const askSave = () => {
        if (!checkValidForm()) return;

        const isNewShop = !formData.id;
        const isProductionChanged = originalShop?.isProduction !== formData.isProduction;

        if (isProductionChanged || (isNewShop && formData.isProduction)) {
            setDialogHeading(formData.isProduction ? t('isProductionTrueHeader') : t('isProductionFalseHeader'));
            setDialogMessage(formData.isProduction ? t('isProductionTrueMessage') : t('isProductionFalseMessage'));
            confirmProductionDialogRef.current?.openDialog?.();
        } else if (!isNewShop && isDirty) {
            confirmationDialogRef.current?.openDialog?.();
        } else {
            handleSave();
        }
    };

    const handleSave = async (): Promise<boolean> => {
        if (!checkValidForm()) return false;

        setLoading(true);
        try {
            const shopToSave = { ...formData } as Shop;
            let savedShopId: string | undefined;

            if (mode == ShopEditorMode.Create) {
                const shopModelToSave = {
                    referenceShop: {
                        referenceShopId: referenceShopId,
                        gatewayName: gateway
                    },
                    shop: shopToSave
                } as CreateShopModel;
                savedShopId = await shopService.createShop(shopModelToSave);
            } else {
                await shopService.saveShop(shopToSave);
                savedShopId = shopToSave.id;
            }

            notifySuccess();
            await loadShopIds();
            onClose(savedShopId);
            return true;
        } catch (error: any) {
            if (error?.status === 406) {
                const validated = error?.properties;
                if (validated) {
                    setValidatedAddress(validated);
                }
                notifyErrorResponse(error);
            } else if (error?.status === 409) {
                onClose();
                notifyWarningResponse(error);
            } else {
                notifyErrorResponse(error);
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleteDialogOpen(false);
        setLoading(true);
        try {
            await shopService.sendShopToRecycleBin(formData.id);
            notifySuccess();
            await loadShopIds();
            const defaultShop = shopIds.find((s) => s.id !== formData.id);
            onClose(defaultShop?.id);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    const canonicalize = (value: unknown): unknown => {
        if (value === '' || value === undefined) return null;
        if (Array.isArray(value)) return value.map(canonicalize);
        if (value !== null && typeof value === 'object') {
            return Object.keys(value as object)
                .sort()
                .reduce<Record<string, unknown>>((acc, key) => {
                    acc[key] = canonicalize((value as Record<string, unknown>)[key]);
                    return acc;
                }, {});
        }
        return value;
    };

    const originalSerialized = useMemo(
        () => (originalShop ? JSON.stringify(canonicalize(originalShop)) : null),
        [originalShop]
    );

    const isDirty = useMemo(() => {
        if (loading || !originalShop) return false;
        const formChanged = JSON.stringify(canonicalize(formData)) !== originalSerialized;
        return mode === ShopEditorMode.Create ? formChanged || !!referenceShopId || !!gateway : formChanged;
    }, [formData, originalSerialized, originalShop, mode, referenceShopId, gateway, loading]);

    useImperativeHandle(ref, () => ({ isDirty, save: handleSave }), [isDirty, handleSave]);

    return (
        <Paper variant="outlined" sx={{ borderRadius: '3px', overflow: 'visible' }}>
            <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                <Box>
                    {mode == ShopEditorMode.Create && (
                        <Section
                            title={t('referenceShopOptional')}
                            expanded={expanded.referenceShop}
                            onToggle={() => toggleSection('referenceShop')}
                        >
                            <Grid container spacing={8}>
                                <Grid item xs={12} md={6}>
                                    <ShopSelector
                                        value={referenceShopId}
                                        onChange={setReferenceShopId}
                                        disabled={loading}
                                        label={t('referenceShop')}
                                        clearable
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormTextField
                                        name={'gatewayName'}
                                        value={gateway}
                                        onChange={(e) => setGateway(e.target.value)}
                                        label={`${t('gateway')}-Name`}
                                        fullWidth
                                        variant="standard"
                                        rules={!!referenceShopId ? [required(t)] : []}
                                    />
                                </Grid>
                            </Grid>
                        </Section>
                    )}

                    <Section
                        title={t('shopInformation')}
                        expanded={expanded.general}
                        onToggle={() => toggleSection('general')}
                    >
                        <Grid container spacing={8}>
                            <Grid item xs={12}>
                                <FormTextField
                                    name={'name'}
                                    label={t('name')}
                                    value={formData.name || ''}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    fullWidth
                                    rules={[required(t)]}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    name={'shopDisplayName'}
                                    label={t('shortName')}
                                    value={formData.shopDisplayName || ''}
                                    onChange={(e) => handleChange('shopDisplayName', e.target.value)}
                                    fullWidth
                                    rules={[required(t), shopDisplayNameRule]}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    name={'externalSystemShopIdentifier'}
                                    label={t('externalSystemShopIdentifier')}
                                    value={formData.externalSystemShopIdentifier || ''}
                                    onChange={(e) => handleChange('externalSystemShopIdentifier', e.target.value)}
                                    fullWidth
                                    rules={[required(t), shopIdRule]}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormTextField
                                    name={'description'}
                                    label={t('description')}
                                    value={formData.description || ''}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <ToggleButton
                                    checked={!!formData.isProduction}
                                    onChange={(value) => handleChange('isProduction', value)}
                                    label={t('isProduction')}
                                />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section title={t('address')} expanded={expanded.address} onToggle={() => toggleSection('address')}>
                        <Grid container spacing={8}>
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    name={'street'}
                                    label={t('street')}
                                    value={formData.address?.street || ''}
                                    onChange={(e) => handleChangeAddress('street', e.target.value)}
                                    fullWidth
                                    rules={streetRules}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    name={'postalCode'}
                                    label={t('postalCode')}
                                    value={formData.address?.postalCode || ''}
                                    onChange={(e) => handleChangeAddress('postalCode', e.target.value)}
                                    fullWidth
                                    rules={postalCodeRules}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    name={'city'}
                                    label={t('city')}
                                    value={formData.address?.city || ''}
                                    onChange={(e) => handleChangeAddress('city', e.target.value)}
                                    fullWidth
                                    rules={cityRules}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    name={'country'}
                                    label={t('country')}
                                    value={formData.address?.country || ''}
                                    onChange={(e) => handleChangeAddress('country', e.target.value)}
                                    fullWidth
                                    rules={countryRules}
                                    required
                                />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section
                        title={t('dataCollectionPeriod')}
                        expanded={expanded.dataCollectionPeriod}
                        onToggle={() => toggleSection('dataCollectionPeriod')}
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <ToggleButton
                                    checked={formData.collectData24Hours}
                                    onChange={(value) => handleChange('collectData24Hours', value)}
                                    label={t('collectData24Hours')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {!formData.collectData24Hours && (
                                    <DayOfWeekFromUntil
                                        daysFromUntil={formData.dataCollectionPeriod || []}
                                        disabled={formData.collectData24Hours}
                                        onChange={(periods) => handleChange('dataCollectionPeriod', periods)}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Section>

                    {hasLicense(ActiveLicenses.ShopAutomation) && (
                        <Section
                            title={t('sensorCameraCredentials')}
                            expanded={expanded.sensorCameraCredentials}
                            onToggle={() => toggleSection('sensorCameraCredentials')}
                        >
                            <Grid container spacing={8}>
                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'username'}
                                        label={t('username')}
                                        value={formData.sensorCameraCredentials?.username || ''}
                                        onChange={(e) =>
                                            handleChangeSensorCameraCredentials('username', e.target.value)
                                        }
                                        fullWidth
                                        rules={
                                            hasAnyCameraCredentials(formData.sensorCameraCredentials)
                                                ? [required(t)]
                                                : []
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormTextField
                                        name={'password'}
                                        label={t('password')}
                                        value={formData.sensorCameraCredentials?.password || ''}
                                        type={showPassword ? 'text' : 'password'}
                                        onChange={(e) =>
                                            handleChangeSensorCameraCredentials('password', e.target.value)
                                        }
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? (
                                                        <i className="bi bi-eye-slash" />
                                                    ) : (
                                                        <i className="bi bi-eye" />
                                                    )}
                                                </IconButton>
                                            )
                                        }}
                                        rules={
                                            hasAnyCameraCredentials(formData.sensorCameraCredentials)
                                                ? [required(t)]
                                                : []
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Section>
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                        px: 3,
                        py: 2.5,
                        flexWrap: 'wrap'
                    }}
                >
                    {mode === ShopEditorMode.Edit && !!formData.id && (
                        <WcnButton variant="red" disabled={loading} onClick={() => setDeleteDialogOpen(true)}>
                            {t('deleteShop')}
                        </WcnButton>
                    )}
                    {mode === ShopEditorMode.Create && (
                        <WcnButton variant="red" disabled={loading} onClick={onCancel}>
                            {t('cancel')}
                        </WcnButton>
                    )}
                    <WcnButton variant="primary" disabled={!formValid || loading} onClick={askSave}>
                        {t('saveChanges')}
                    </WcnButton>
                </Box>
            </FormContainer>

            <ConfirmationDialog
                dialogRef={confirmProductionDialogRef}
                heading={dialogHeading}
                message={dialogMessage}
                isPrimaryButtonDisabled={loading}
                onConfirm={!formData.id ? handleSave : confirmationDialogRef?.current?.openDialog}
            />

            <ConfirmationDialog
                dialogRef={confirmationDialogRef}
                heading={t('saveShop')}
                message={t('saveShopConfirmMessage')}
                isPrimaryButtonDisabled={loading}
                onConfirm={handleSave}
            />
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('recycleShopMessage', { item: formData.name })}
                onConfirm={handleDelete}
                onCancel={() => setDeleteDialogOpen(false)}
            />
            <LoadingOverlay open={loading || loadingData} />
        </Paper>
    );
};

export default ShopInformation;
